import { useState, type FormEvent, type MouseEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Building2, Plus, Search } from "lucide-react";
import { useAdminCompanies, useCreateCompany } from "../../api/admin";
import { extractErrorMessage } from "../../api/errors";
import type { Company } from "../../api/types";
import { startActingAsCompany } from "../../auth/session";
import { DataTable } from "../../components/DataTable";
import { StatusBadge } from "../../components/StatusBadge";

function actionButtonClass(kind: "primary" | "secondary" = "secondary"): string {
  if (kind === "primary") {
    return "inline-flex items-center justify-center rounded-md bg-riot-gold-400 px-3 py-1.5 text-xs font-medium text-riot-navy-950 hover:bg-riot-gold-500 active:bg-riot-gold-600";
  }

  return "inline-flex items-center justify-center rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-100 dark:hover:bg-riot-navy-700";
}

export function AdminCompaniesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading } = useAdminCompanies();
  const createCompany = useCreateCompany();
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [createError, setCreateError] = useState("");
  const [createdCompany, setCreatedCompany] = useState<Company | null>(null);

  function enterCompanyWorkspace(company: Pick<Company, "tenantId" | "companyName">, nextPath: string): void {
    startActingAsCompany(company.tenantId, company.companyName);
    queryClient.clear();
    navigate(nextPath);
  }

  function stopRowNavigation(e: MouseEvent<HTMLButtonElement>): void {
    e.stopPropagation();
  }

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setCreateError("");

    try {
      const resp = await createCompany.mutateAsync({
        companyName: name.trim(),
        contactEmail: email.trim(),
      });
      setCreatedCompany(resp.data);
      setShowCreate(false);
      setName("");
      setEmail("");
    } catch (err) {
      setCreateError(extractErrorMessage(err, "Failed to create company"));
    }
  }

  const columns = [
    {
      key: "companyName",
      header: "Company",
      sortable: true,
      render: (row: Company) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-white">{row.companyName}</div>
          <div className="mt-0.5 font-mono text-[11px] text-gray-400 dark:text-riot-navy-400">{row.tenantId}</div>
        </div>
      ),
    },
    { key: "contactEmail", header: "Email", sortable: true },
    {
      key: "subscriptionTier",
      header: "Tier",
      sortable: true,
      render: (row: Company) => <span className="font-medium capitalize">{row.subscriptionTier || "-"}</span>,
    },
    {
      key: "status",
      header: "Status",
      render: (row: Company) => <StatusBadge status={row.status} />,
    },
    {
      key: "maxDevices",
      header: "Max Devices",
      render: (row: Company) => <span>{row.maxDevices || 0}</span>,
    },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      render: (row: Company) => new Date(row.createdAt).toLocaleDateString(),
    },
    {
      key: "workspace",
      header: "Actions",
      width: "280px",
      render: (row: Company) => (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={(e) => {
              stopRowNavigation(e);
              enterCompanyWorkspace(row, "/");
            }}
            className={actionButtonClass("primary")}
            aria-label={`Masquerade as ${row.companyName}`}
          >
            Masquerade
          </button>
          <button
            type="button"
            onClick={(e) => {
              stopRowNavigation(e);
              enterCompanyWorkspace(row, "/groups");
            }}
            className={actionButtonClass()}
            aria-label={`Manage groups for ${row.companyName}`}
          >
            Groups
          </button>
          <button
            type="button"
            onClick={(e) => {
              stopRowNavigation(e);
              navigate(`/admin/companies/${row.tenantId}`);
            }}
            className={actionButtonClass()}
            aria-label={`Open ${row.companyName} details`}
          >
            Details
          </button>
        </div>
      ),
    },
  ];

  const companies = data?.data ?? [];

  return (
    <div className="space-y-4">
      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-riot-navy-700 dark:bg-riot-navy-800">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-riot-gold-100 text-riot-gold-600 dark:bg-riot-gold-500/10 dark:text-riot-gold-400">
                <Building2 size={22} />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-riot-gold-600 dark:text-riot-gold-400">
                  Platform companies
                </p>
                <h1 className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white">
                  Add a company, find it quickly, and jump into its workspace
                </h1>
                <p className="mt-1 max-w-3xl text-sm text-gray-500 dark:text-riot-navy-300">
                  This is the control point for company onboarding. Search the full company list, open a company profile,
                  or masquerade directly into a company so dashboard, devices, groups, and other tenant pages reflect that
                  company context.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 dark:border-riot-navy-700 dark:bg-riot-navy-900/60">
                <div className="mb-1 text-xs uppercase tracking-wide text-gray-400 dark:text-riot-navy-400">Companies</div>
                <div className="text-xl font-semibold text-gray-900 dark:text-white">{companies.length}</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 dark:border-riot-navy-700 dark:bg-riot-navy-900/60">
                <div className="mb-1 text-xs uppercase tracking-wide text-gray-400 dark:text-riot-navy-400">Search</div>
                <div className="flex items-center gap-2 text-gray-700 dark:text-riot-navy-100">
                  <Search size={14} className="text-riot-gold-500" />
                  Company, email, or tenant ID
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 dark:border-riot-navy-700 dark:bg-riot-navy-900/60">
                <div className="mb-1 text-xs uppercase tracking-wide text-gray-400 dark:text-riot-navy-400">Fast actions</div>
                <div className="text-gray-700 dark:text-riot-navy-100">Masquerade, manage groups, or open details</div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 lg:w-[220px] lg:items-end lg:text-right">
            <button
              onClick={() => {
                setShowCreate(true);
                setCreateError("");
              }}
              className="inline-flex w-fit max-w-[150px] flex-wrap items-center justify-center gap-x-2 gap-y-1 self-start rounded-md bg-riot-gold-400 px-3 py-2 text-xs font-medium leading-tight text-center text-riot-navy-950 hover:bg-riot-gold-500 active:bg-riot-gold-600 lg:self-end"
            >
              <Plus size={15} /> Add company
            </button>
            <p className="max-w-[220px] text-xs leading-relaxed text-gray-500 dark:text-riot-navy-300">
              Start with the company record here, then use its detail page or workspace actions to continue setup.
            </p>
          </div>
        </div>
      </section>

      {createdCompany && (
        <section className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-500/30 dark:bg-emerald-500/10">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-medium text-emerald-900 dark:text-emerald-200">
                {createdCompany.companyName} has been created.
              </p>
              <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-300">
                You can review the company, enter its workspace, or start structuring device groups right away.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => enterCompanyWorkspace(createdCompany, "/")}
                className={actionButtonClass("primary")}
              >
                Masquerade now
              </button>
              <button
                type="button"
                onClick={() => enterCompanyWorkspace(createdCompany, "/groups")}
                className={actionButtonClass()}
              >
                Manage groups
              </button>
              <button
                type="button"
                onClick={() => navigate(`/admin/companies/${createdCompany.tenantId}`)}
                className={actionButtonClass()}
              >
                Open details <ArrowRight size={14} className="ml-1" />
              </button>
            </div>
          </div>
        </section>
      )}

      {showCreate && (
        <form
          onSubmit={(e) => void handleCreate(e)}
          className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-riot-navy-700 dark:bg-riot-navy-800"
        >
          <div className="mb-4">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Add company</h2>
            <p className="mt-1 text-xs text-gray-500 dark:text-riot-navy-300">
              Create the tenant record first. Tier, status, limits, and deeper setup can be adjusted immediately
              afterward from the company detail screen.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="company-name" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                Company name
              </label>
              <input
                id="company-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. BetaLogistics"
                required
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
              />
            </div>
            <div>
              <label htmlFor="company-email" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                Contact email
              </label>
              <input
                id="company-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ops@company.com"
                required
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-riot-gold-500 focus:outline-none focus:ring-1 focus:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
              />
            </div>
          </div>

          {createError && (
            <p className="mt-3 text-xs text-red-600 dark:text-red-400">{createError}</p>
          )}

          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setShowCreate(false);
                setCreateError("");
              }}
              className={actionButtonClass()}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createCompany.isPending || !name.trim() || !email.trim()}
              className={actionButtonClass("primary")}
            >
              {createCompany.isPending ? "Creating..." : "Create company"}
            </button>
          </div>
        </form>
      )}

      {isLoading ? (
        <div className="py-20 text-center text-sm text-gray-400 dark:text-riot-navy-400">Loading companies...</div>
      ) : (
        <DataTable
          title="All companies"
          totalCount={data?.pagination.count ?? companies.length}
          columns={columns}
          data={companies}
          keyExtractor={(company) => company.tenantId}
          onRowClick={(company) => navigate(`/admin/companies/${company.tenantId}`)}
          selectable
          filters={{
            searchPlaceholder: "Search companies",
            searchFields: [
              { value: "companyName", label: "Company name" },
              { value: "contactEmail", label: "Contact email" },
              { value: "tenantId", label: "Tenant ID" },
            ],
            statusOptions: [
              { value: "active", label: "Active" },
              { value: "pending", label: "Pending" },
              { value: "suspended", label: "Suspended" },
            ],
          }}
          actions={
            <button
              onClick={() => {
                setShowCreate(true);
                setCreateError("");
              }}
              className="flex items-center gap-1.5 rounded-md bg-riot-gold-400 px-3.5 py-1.5 text-xs font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600"
            >
              <Plus size={14} /> New company
            </button>
          }
        />
      )}
    </div>
  );
}
